import { useCallback, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';

import { Capacitor, type PluginListenerHandle } from '@capacitor/core';

import { APP_MODAL, APP_STATUS, checkUpgrade, setAppModal } from '../components/updateApp/appStatusSlice';
import { environment } from '../environment/environment';
import { getDidOnboarding, isNative } from '../helpers/device.helper';
import { initNativeNotification, initWebNotification } from '../helpers/notification.helper';
import { getDartAlertInfo } from '../pages/guest/firstAlertsSlice';
import { endGuestLeftChat } from '../pages/guest/guestChatSlice';
import { loadGuestData } from '../pages/guest/guestSlice';
import { useAppDispatch, useAppSelector } from '../store';
import { useChunkErrorReload } from './useChunkErrorReload';

export const useInitApp = () => {
  useChunkErrorReload();
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const appOrgCode = useAppSelector((state) => state.appStatusSlice.appOrgCode);
  const guestId = useAppSelector((state) => state.guestSlice.guestInfo?.guest_id);
  const currentUser = useAppSelector((state) => state.advocateAuthSlice.currentUser);
  const appUpdateDismissed = useAppSelector((state) => state.appStatusSlice.appUpdateDismissed);

  const handleAppUpgrade = useCallback(async () => {
    const checkUpgradeResponse = await dispatch(checkUpgrade());
    if (
      !appUpdateDismissed &&
      checkUpgrade.fulfilled.match(checkUpgradeResponse) &&
      (checkUpgradeResponse.payload === APP_STATUS.APP_EXPIRED ||
        checkUpgradeResponse.payload === APP_STATUS.APP_UPDATE_AVAILABLE)
    ) {
      dispatch(setAppModal(APP_MODAL.UPDATE));
    }
  }, [dispatch, appUpdateDismissed]);

  // Checks for app upgrade
  useEffect(() => {
    if (isNative()) handleAppUpgrade();
  }, [handleAppUpgrade]);

  // Triggers notification initialization in the subsequent app restarts
  useEffect(() => {
    if (getDidOnboarding()) {
      if (isNative()) {
        initNativeNotification();
      } else {
        initWebNotification();
      }
    }
  }, []);

  // Ends left chat and loads guest data
  useEffect(() => {
    if (getDidOnboarding()) {
      dispatch(endGuestLeftChat());
      dispatch(loadGuestData());
    }
  }, [dispatch]);

  // Adds listener for background to foreground changes
  // to re-initiate native push notification and get latest dart event info
  useEffect(() => {
    let appListener: PluginListenerHandle;

    if (isNative() && Capacitor.isPluginAvailable('App')) {
      (async () => {
        const { App } = await import('@capacitor/app');
        appListener = await App.addListener('appStateChange', async ({ isActive }) => {
          if (isActive) {
            initNativeNotification();
            dispatch(getDartAlertInfo());
          }
        });
      })();
    }

    return () => {
      appListener?.remove();
    };
  }, [dispatch]);

  // Sets the PostHog user identify
  useEffect(() => {
    const data = {
      guest_id: guestId,
      organization_code: appOrgCode,
      platform: Capacitor.getPlatform(),
      version: environment.versionNumber,
      role: currentUser?.role ? 'Advocate' : 'Guest',
      advocate_id: currentUser?.user_id,
    };

    if (guestId) {
      if (currentUser?.role) {
        posthog?.identify(guestId, {
          ...data,
          service_provider_organization_id: currentUser?.service_provider_organization_id,
        });
      } else {
        posthog?.identify(guestId, data);

        const eventName = 'reset_role_to_guest';
        posthog?.capture(eventName, {
          $unset: ['service_provider_organization_id'],
        });
      }
    } else {
      posthog?.reset();
    }
  }, [
    appOrgCode,
    currentUser?.role,
    currentUser?.service_provider_organization_id,
    currentUser?.user_id,
    guestId,
    posthog,
  ]);
};
